/**
 * gst_calculator_controller.js — context-aware GST calculator (Stimulus).
 *
 * Scoped to the GST calculator's <div class="calc-layout"> (contains the
 * calculator form, the context select, the related-tool nudge, and the results
 * panel). The form is live on arrival: entering an amount (or toggling add/remove
 * GST) recalculates, and submitting scrolls to the results. Picking a context is
 * optional; it adds situation help and sets the likely add/remove direction,
 * recalculating whatever amount is already typed.
 *
 * The results always read ex GST, GST, inc GST, whichever direction was asked.
 *
 * Replaces an end-of-body inline <script> that bound listeners via getElementById
 * + addEventListener and an inline onchange="updateContext()". Both break after a
 * Turbo <body> swap (DOMContentLoaded never re-fires; bound nodes are replaced).
 * Stimulus re-connects after each swap and binds the listeners through data-action,
 * so no manual lifecycle bookkeeping is needed. Element IDs are preserved.
 */

import { Controller } from "@hotwired/stimulus";
import { GST_RATE, gstFromInclusive, gstOnExclusive } from "./_gst.js";

    var CONTEXTS = {
        quoting: {
            title: "Quoting a Job for a Customer",
            description: "Calculate GST on your quote amount. Remember: customers pay GST on the final price, including materials and labor.",
            amountHelp: "Enter your quote amount (excluding GST)",
            calcTypeHelp: "Add GST to show customer the total price they'll pay",
            defaultCalcType: "add"
        },
        materials: {
            title: "Bought Materials for a Job",
            description: "You paid GST on materials and can claim it back as a credit. Your customer pays GST once on the final installed price.",
            amountHelp: "Enter the material cost you paid (including GST)",
            calcTypeHelp: "Remove GST to see the net material cost for your calculations",
            defaultCalcType: "remove"
        },
        invoice: {
            title: "Checking an Invoice You Received",
            description: "Verify the GST amount on supplier invoices. You can claim these GST credits on your BAS.",
            amountHelp: "Enter the total invoice amount you received",
            calcTypeHelp: "Remove GST to check the pre-GST amount is correct",
            defaultCalcType: "remove"
        },
        customer: {
            title: "Customer Asking About GST",
            description: "Customers often question GST on materials. This calculator helps you show them the numbers.",
            amountHelp: "Enter the material cost amount to demonstrate GST calculation",
            calcTypeHelp: "Show how GST works on materials (usually add GST for customer demonstration)",
            defaultCalcType: "add",
            suggestMaterialsExplainer: true
        }
    };

export default class extends Controller {
        connect() {
            var amount = this._q('#amount');
            if (amount) amount.value = '';
            this._hide('#results');
        }

        updateContext() {
            var ctx = CONTEXTS[this._q('#context').value];

            this._hide('#contextHelp');
            this._hide('#relatedTool');
            this._q('#amountHelp').textContent = ctx ? ctx.amountHelp : '';
            this._q('#calcTypeHelp').textContent = ctx ? ctx.calcTypeHelp : '';
            if (!ctx) return;

            this._q('#contextTitle').textContent = ctx.title;
            this._q('#contextDescription').textContent = ctx.description;
            this._show('#contextHelp');

            var radio = this._q('input[name="calcType"][value="' + ctx.defaultCalcType + '"]');
            if (radio) radio.checked = true;

            if (ctx.suggestMaterialsExplainer) {
                this._q('#relatedToolText').textContent =
                    'For detailed GST explanations on materials, use our visual Materials GST Explainer tool.';
                this._show('#relatedTool');
            }

            this.calculate();
        }

        calculate() {
            var amount = parseFloat(this._q('#amount').value);
            var calcType = this._q('input[name="calcType"]:checked').value;

            if (isNaN(amount) || amount <= 0) {
                this._hide('#results');
                return;
            }

            var exclusive, gst, formula;

            if (calcType === 'add') {
                exclusive = amount;
                gst = gstOnExclusive(amount);
                formula = '$' + amount.toFixed(2) + ' × ' + (GST_RATE * 100) + '% = $' + gst.toFixed(2) + ' GST';
            } else {
                var split = gstFromInclusive(amount);
                exclusive = split.exclusive;
                gst = split.gst;
                formula = '$' + amount.toFixed(2) + ' ÷ ' + (1 + GST_RATE).toFixed(2) + ' = $' + exclusive.toFixed(2) + ' (excludes GST)';
            }

            this._q('#exAmount').textContent = '$' + exclusive.toFixed(2);
            this._q('#gstAmount').textContent = '$' + gst.toFixed(2);
            this._q('#incAmount').textContent = '$' + (exclusive + gst).toFixed(2);
            this._q('#formula').textContent = formula;

            this._show('#results');
        }

        onSubmit(event) {
            event.preventDefault();
            this.calculate();
            var results = this._q('#results');
            if (results) results.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        // --- helpers ---
        _q(selector) { return this.element.querySelector(selector); }
        _show(selector) { var el = this._q(selector); if (el) el.classList.remove('d-none'); }
        _hide(selector) { var el = this._q(selector); if (el) el.classList.add('d-none'); }
}
